/* eslint-disable */

import axios from 'axios';
import axiosConfig from '../axiosConfig';
import {
	ChatRequest,
	ChatResponse,
	ChatSession,
	ChatSessionHistory,
	ChatSessionsResponse,
	GenericResponse,
	InovationChatRequest,
	InovationChatResponse,
} from '../types';

const API_KEY =
	process.env.REACT_APP_INNOVATION_AI_API_KEY ??
	'c2051712053c930e9345e66e24a58de140f33e8be8ba6c01b242358774ef4f81';

// Returns the first segment of the hostname (before the first '.')
const getZonePrefix = (): string => {
	try {
		const hostname =
			window.location.hostname ||
			(window.location.host && window.location.host.split(':')[0]) ||
			'';
		return hostname.split('.')[0] ?? '';
	} catch (e) {
		return '';
	}
};

const cfBase = (() => {
	const envUrl = process.env.REACT_APP_INNOVATION_AI_API_URL;
	if (window.location.host.includes('.zeroedin.com:7776')) {
		return 'https://ai-analyst.zeroedin-dev.com/chat';
	}
	return envUrl ?? 'https://ai-analyst.zeroedin-dev.com/chat';
})();

let cachedToken: { token: string; expires_at: number } | null = null;

/**
 * Fetches a Bearer token from the token-exchange endpoint.
 * Caches the token until it expires.
 */
async function getBearerToken(): Promise<string> {
	const now = Date.now();

	// Return cached token if still valid (with 1-minute buffer before expiry)
	if (cachedToken && cachedToken.expires_at > now + 60000) {
		return cachedToken.token;
	}

	try {
		const response = await axiosConfig.instance.post('app/mcp/token-exchange');
		const data = response.data;

		const token = data.token || data.data?.token;
		const expiresAt = new Date(data.expires_at || data.data?.expires_at).getTime();

		cachedToken = { token, expires_at: expiresAt };
		return token;
	} catch (err) {
		console.error('Failed to fetch Bearer token:', err);
		throw new Error('Unable to obtain Bearer token for Innovation AI');
	}
}

export class ChatActions {
	public static SendMessage(request: ChatRequest): Promise<GenericResponse<ChatResponse>> {
		const url = 'app/ai/chat';

		return axiosConfig.instance.post(url, request).then((res) => {
			return res.data as GenericResponse<ChatResponse>;
		});
	}

	/**
	 * Sends a message to the Innovation AI endpoint (used by Analyst AI page).
	 * Base URL is constructed from env vars:
	 *  - REACT_APP_INNOVATION_AI_API_ID
	 *  - REACT_APP_INNOVATION_AI_ENVIRONMENT
	 * Falls back to the hardcoded URL if env vars are missing.
	 */
	public static async sendInovationAIMessage(
		request: InovationChatRequest
	): Promise<InovationChatResponse> {
		const cfUrl = cfBase;

		// Minimal axios to avoid XSRF header and cookies
		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.post(cfUrl, request, {
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${bearerToken}`,
						'x-actor-id': request.userId,
						'zonePrefix': getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;
					if (res.status === 202) {
						const jobId = data?.job_id ?? data?.jobId ?? data?.job ?? '';
						return {
							session_id: data?.session_id ?? '',
							job_id: jobId,
							response: 'the response is taking longer than expected to complete',
							status: 'pending',
							trace: data?.trace ?? [],
						} as InovationChatResponse;
					}

					if (typeof data === 'string') {
						return {
							session_id: '',
							response: data,
							status: 'complete',
							trace: [],
						} as InovationChatResponse;
					}

					return data as InovationChatResponse;
				})
				.catch((err) => {
					console.error('Direct CloudFront call failed:', err?.message ?? err);
					return {
						session_id: '',
						response: '',
						status: 'error',
						trace: [],
					} as InovationChatResponse;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				session_id: '',
				response: '',
				status: 'error',
				trace: [],
			} as InovationChatResponse;
		}
	}

	/**
	 * Polls the Innovation AI status endpoint for a long-running job_id until completion.
	 */
	// NOT IN USE
	public static async getInovationAIStatus(jobId: string): Promise<InovationChatResponse> {
		const cfBaseUrl =
			process.env.REACT_APP_INNOVATION_AI_API_URL ??
			'https://ai-analyst.zeroedin-dev.com/chat';
		const statusUrl = cfBaseUrl.endsWith('/chat')
			? `${cfBaseUrl}/status`
			: `${cfBaseUrl}/chat/status`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 100000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.get(statusUrl, {
					params: { job_id: jobId },
					headers: {
						Authorization: `Bearer ${bearerToken}`,
						zonePrefix: getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;
					if (res.status === 200) {
						if (typeof data === 'string') {
							return {
								session_id: '',
								response: data,
								status: 'complete',
								trace: [],
							} as InovationChatResponse;
						}

						return {
							session_id: data?.session_id ?? '',
							job_id: data?.job_id ?? jobId,
							response: data?.response ?? (typeof data === 'string' ? data : ''),
							status:
								data?.status === 'complete'
									? 'complete'
									: data?.status ?? 'pending',
							trace: data?.trace ?? [],
						} as InovationChatResponse;
					}

					return {
						session_id: '',
						response: '',
						status: 'error',
						trace: [],
					} as InovationChatResponse;
				})
				.catch((err) => {
					console.error('Direct status check failed:', err?.message ?? err);
					return {
						session_id: '',
						response: '',
						status: 'error',
						trace: [],
					} as InovationChatResponse;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				session_id: '',
				response: '',
				status: 'error',
				trace: [],
			} as InovationChatResponse;
		}
	}

	public static async getInovationAIChatHistory(
		jobId: string,
		sessionId: string
	): Promise<ChatSession> {
		let statusUrl = cfBase.endsWith('/chat') ? `${cfBase}/history` : `${cfBase}/chat/history`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.get(statusUrl, {
					params: {
						job_id: jobId,
						session_id: sessionId,
					},
					headers: {
						Authorization: `Bearer ${bearerToken}`,
						zonePrefix: getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;

					if (res.status === 200) {
						if (typeof data === 'string') {
							return {
								session_id: '',
								messages: [
									{
										role: 'assistant',
										content: data,
										timestamp: Date.now(),
									},
								],
							} as ChatSession;
						}

						return {
							session_id: data?.session_id ?? '',
							messages: data?.messages ?? [],
						} as ChatSession;
					}

					return {
						session_id: '',
						messages: [],
					} as ChatSession;
				})
				.catch((err) => {
					console.error('Direct status check failed:', err?.message ?? err);

					return {
						session_id: '',
						messages: [],
					} as ChatSession;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				session_id: '',
				messages: [],
			} as ChatSession;
		}
	}

	/**
	 * Get all chat sessions for the current user
	 * Currently mocked - will switch to real endpoint when available
	 * Real endpoint: GET /chat/sessions
	 */
	public static async getInovationAIChatSessions(
		limit = 25,
		userId = ''
	): Promise<ChatSessionsResponse> {
		const sessionsUrl = cfBase.endsWith('/chat')
			? `${cfBase}/sessions`
			: `${cfBase}/chat/sessions`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.get(sessionsUrl, {
					params: {
						limit,
					},
					headers: {
						'x-actor-id': userId,
						'Authorization': `Bearer ${bearerToken}`,
						'zonePrefix': getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;

					if (res.status === 200) {
						return {
							sessions: data?.sessions ?? [],
						} as ChatSessionsResponse;
					}

					return {
						sessions: [],
					} as ChatSessionsResponse;
				})
				.catch((err) => {
					console.error('Failed to fetch chat sessions:', err?.message ?? err);

					return {
						sessions: [],
					} as ChatSessionsResponse;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				sessions: [],
			} as ChatSessionsResponse;
		}
	}

	/**
	 * Delete a chat session by ID
	 * Real endpoint: DELETE /chat/sessions/:sessionId
	 */
	public static async deleteInovationAIChatSession(
		sessionId: string,
		userId = ''
	): Promise<boolean> {
		const deleteUrl = cfBase.endsWith('/chat')
			? `${cfBase}/sessions/${sessionId}`
			: `${cfBase}/chat/sessions/${sessionId}`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.delete(deleteUrl, {
					headers: {
						'x-actor-id': userId,
						'Authorization': `Bearer ${bearerToken}`,
						'zonePrefix': getZonePrefix(),
					},
				})
				.then((res) => {
					return res.status === 200 || res.status === 204;
				})
				.catch((err) => {
					console.error('Failed to delete chat session:', err?.message ?? err);
					return false;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return false;
		}
	}
}
